// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable @quince/no-direct-localstorage */
// https://lastbrand.atlassian.net/browse/ECOMP-4497
import { AxiosResponse } from 'axios'

import { keyBy, omit } from '@src/utils/functions/utils'

import isBrowser from '../../utils/isBrowser'
import { getInventory } from '../inventoryService/inventory.service'

import { updateNewCheckoutInLocalStorage } from './cartCheckoutService.helper'
import { getProductData } from './checkout.service'
import {
  CART_CHECKOUT_OBJ_KEY,
  CART_AUTH_CODE_KEY,
  CART_AUTH_CODE_RES_KEY,
  LOCAL_STORAGE_CART_OBJ_KEY,
  LOCAL_STORAGE_NEW_CART_ID_KEY,
  CHECKOUT_AUTH_CODE_KEY,
  CHECKOUT_AUTH_CODE_RES_KEY,
} from './constants'

export function setCartAuthCodeFromHeader(res: AxiosResponse) {
  const cartAuthCode = res.headers[CART_AUTH_CODE_RES_KEY]
  const checkoutAuthCode = res.headers[CHECKOUT_AUTH_CODE_RES_KEY]
  if (cartAuthCode) {
    localStorage.setItem(CART_AUTH_CODE_KEY, cartAuthCode)
  }
  if (checkoutAuthCode) {
    localStorage.setItem(CHECKOUT_AUTH_CODE_KEY, checkoutAuthCode)
  }
}

export function getCartAuthCodeFromHeader() {
  if (!isBrowser()) return null

  const code = localStorage.getItem(CART_AUTH_CODE_KEY)
  return code
}

export function getCheckoutAuthCodeFromHeader() {
  if (!isBrowser()) return { checkoutAuthCode: '', cartAuthCode: '' }

  const checkoutAuthCode = localStorage.getItem(CHECKOUT_AUTH_CODE_KEY)
  const cartAuthCode = localStorage.getItem(CART_AUTH_CODE_KEY)
  return { checkoutAuthCode, cartAuthCode }
}
export async function getInventoryDataForCart(data: any) {
  if (
    data.lineItems?.length === 0 &&
    (!data.saveForLaterItems?.length || data.saveForLaterItems.length === 0) &&
    (!data.outOfStockLineItems?.length || data.outOfStockLineItems.length === 0)
  )
    return {}

  const variantIds = data.lineItems?.map((item: any) => item.variantId) || []
  const saveForLaterVariantIds = data.saveForLaterItems?.map((item: any) => item.variantId) || []
  const outOfStockVariantIds = data.outOfStockLineItems?.map((item: any) => item.variantId) || []
  const allVariantIds = [...variantIds, ...saveForLaterVariantIds, ...outOfStockVariantIds].join(',')
  const inventoryData = await getInventory({ variantIds: allVariantIds })
  return inventoryData
}

export async function getProductDataForCart(data: any) {
  if (!isBrowser()) return {}
  const productIdMap =
    data?.lineItems?.reduce((acc: any, lineItem: any) => {
      return {
        ...acc,
        [lineItem.variantId]: lineItem?.variantMetaData?.productId,
      }
    }, {}) || {}

  const saveForLaterProductIdMap =
    data?.saveForLaterItems?.reduce((acc: any, lineItem: any) => {
      return {
        ...acc,
        [lineItem.variantId]: lineItem?.variantMetaData?.productId,
      }
    }, {}) || {}

  const outOfStockProductIdMap =
    data?.outOfStockLineItems?.reduce((acc: any, lineItem: any) => {
      return {
        ...acc,
        [lineItem.variantId]: lineItem?.variantMetaData?.productId,
      }
    }, {}) || {}

  const totalProductIdMap = { ...productIdMap, ...saveForLaterProductIdMap, ...outOfStockProductIdMap }

  const productIds = Object.values(totalProductIdMap)
  const products =
    productIds.length > 0
      ? await getProductData({
          productIds,
        })
      : {}
  const productMap = keyBy(products, 'id')

  const productDataWithVariantMap = Object.entries(totalProductIdMap).reduce(
    (acc: any, [variantId, productId]: any) => {
      return {
        ...acc,
        [variantId]: {
          productData: omit(productMap[productId], [`variants`]),
          selectedVariant: productMap[productId]?.variants?.find((variant: any) => {
            return variant?.id?.toString() === variantId
          }),
        },
      }
    },
    {},
  )
  return productDataWithVariantMap
}

export function mergeInventoryDataInCart(data: any, inventoryData: any) {
  const lineItems = data.lineItems.map((item: any) => {
    return {
      ...item,
      inventoryData: inventoryData[item.variantId],
    }
  })

  return {
    ...data,
    lineItems,
  }
}

function mergeProductDataInCart(data: any, _productData: any) {
  const lineItems = data.lineItems.map((lineItem: any) => {
    const variantId = lineItem.variantId
    const product = _productData[variantId]
    const productMetaData = product.selectedVariant
    const slug = product.productData.slug

    return {
      ...lineItem,
      productMetaData,
      slug,
    }
  })

  const saveForLaterlineItems =
    data?.saveForLaterItems?.map((lineItem: any) => {
      const variantId = lineItem.variantId
      const product = _productData[variantId]
      const productMetaData = product?.selectedVariant
      const slug = product?.productData?.slug

      return {
        ...lineItem,
        productMetaData,
        slug,
      }
    }) || []

  const outOfStocklineItems =
    data?.outOfStockLineItems?.map((lineItem: any) => {
      const variantId = lineItem.variantId
      const product = _productData[variantId]
      const productMetaData = product?.selectedVariant
      const slug = product?.productData?.slug

      return {
        ...lineItem,
        productMetaData,
        slug,
      }
    }) || []

  return {
    ...data,
    lineItems: lineItems,
    saveForLaterItems: saveForLaterlineItems,
    outOfStockLineItems: outOfStocklineItems,
  }
}

export async function getDataWithProductAndInventory(data: any) {
  try {
    const cartDataWithoutFulfillmentDetails = data?.cart
    const [productData, inventoryData] = await Promise.all([
      getProductDataForCart(cartDataWithoutFulfillmentDetails),
      getInventoryDataForCart(cartDataWithoutFulfillmentDetails),
    ])

    const cartData = cartDataWithoutFulfillmentDetails

    const inventoryDataWithCart = mergeInventoryDataInCart(cartData, inventoryData)
    const mergedData = mergeProductDataInCart(inventoryDataWithCart, productData)
    // side effect update in local storage
    updateNewCheckoutInLocalStorage({ ...data, cart: mergedData })

    return mergedData
  } catch (error) {
    console.error(error)
    updateNewCheckoutInLocalStorage(data)
    return data?.cart
  }
}

export const removeCheckoutIdFromLocalStorage = () => {
  window.localStorage.removeItem(LOCAL_STORAGE_NEW_CART_ID_KEY)
  window.localStorage.removeItem(LOCAL_STORAGE_CART_OBJ_KEY)

  window.localStorage.removeItem(CART_CHECKOUT_OBJ_KEY)
  window.localStorage.removeItem(CART_CHECKOUT_OBJ_KEY)
}

export function setCartAuthCodeToLocalStorage(code: string) {
  localStorage.setItem(CART_AUTH_CODE_KEY, code)
}
